// note/NoteModal.js
import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import useScrollLock from "../../hooks/useScrollLock";
import useKeyScrollLock from "../../hooks/useKeyScrollLock";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import NoteCard from "./NoteCard";
import PhotoLightbox from "../PhotoLightbox";
import Spinner from "../../ui/Spinner";

export default function NoteModal({
  open,
  onClose,
  note,
  loading,
  onNoteDeleted,
  onNoteUpdated,
  initialCommentsOpen,
  highlightCommentId,
}) {
  /**
   * Keeps track of if the photo view is open or not
   */
  const [isPhotoView, setIsPhotoView] = useState(false);
  /**
   * Which photo index in the note's images is selected
   */
  const [photoIndex, setPhotoIndex] = useState(0);
  /**
   * Driven by NoteCard via onAnyMenuOpenChange
   * Used to toggle scroll locks for drop down menus
   */
  const [anyMenuOpen, setAnyMenuOpen] = useState(false);

  /**
   * Refernce to the note card container, used to render the dropdown menus within the container
   */
  const cardRef = useRef(null);

  // Locks
  useScrollLock(cardRef, anyMenuOpen);
  useKeyScrollLock(anyMenuOpen);
  useBodyScrollLock(open);

  /**
   * Handle Escape key to close modal (only when photo view is not open)
   */
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && !isPhotoView) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open, isPhotoView, onClose]);

  /**
   * Listens to resize and orientationchange and returns current viewport width and height
   */

  const images = Array.isArray(note?.images) ? note.images : [];
  const hasImages = images.length > 0;

  /**
   * Driven by NoteCard component
   * handler when user clicks on image box in the note card
   */
  const openPhotoViewAt = useCallback(
    (i) => {
      if (!hasImages) return;
      let next = Number.isInteger(i) ? i : 0;
      if (next < 0) next = 0;
      if (next >= images.length) next = images.length - 1;
      setPhotoIndex(next);
      setIsPhotoView(true);
    },
    [hasImages, images.length],
  );

  /**
   * Handles closing of the photo viewer and then the NoteModal
   */
  const handleCloseLightbox = useCallback(() => {
    setIsPhotoView(false);
  }, []);

  if (!open) return null;

  const styleBlock = `
    .pretty-scroll { scrollbar-width: none; }
    .pretty-scroll::-webkit-scrollbar { width: 0; height: 0; }
    @keyframes dropdownIn { from { opacity: 0; transform: translateY(-4px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
    .dropdown-anim { animation: dropdownIn .16s ease-out; }
    .overscroll-contain { overscroll-behavior: contain; }
  `;

  const modal = (
    <div className="fixed inset-0 z-[60]">
      <style>{styleBlock}</style>

      {/* Modal backdrop — hide while photo viewer is open to avoid double dimming */}
      {!isPhotoView && (
        <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      )}

      {/* Centered Note card — hidden while the photo viewer is open */}
      {!isPhotoView && (
        <div className="absolute inset-0 flex items-center justify-center p-4 overscroll-contain pointer-events-none">
          <div className="relative w-full max-w-[calc(100vw-2rem)] sm:max-w-[640px] pointer-events-auto">
            <div
              ref={cardRef}
              className="relative rounded-2xl shadow-2xl bg-surface-elevated border border-border flex flex-col overflow-y-auto pretty-scroll"
            >
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Spinner />
                </div>
              ) : note ? (
                <NoteCard
                  note={note}
                  showClose
                  onClose={onClose}
                  onRequestPhotoView={openPhotoViewAt}
                  menuBoundaryRef={cardRef}
                  onAnyMenuOpenChange={setAnyMenuOpen}
                  isOwner={note?.isOwner}
                  onNoteDeleted={onNoteDeleted}
                  onNoteUpdated={onNoteUpdated}
                  initialCommentsOpen={initialCommentsOpen}
                  highlightCommentId={highlightCommentId}
                />
              ) : (
                <div className="flex items-center justify-center p-12 text-text-muted">
                  Note not found
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo viewer lightbox */}
      <PhotoLightbox
        open={isPhotoView}
        images={images}
        index={photoIndex}
        onIndexChange={setPhotoIndex}
        onClose={handleCloseLightbox}
      />
    </div>
  );

  return createPortal(modal, document.body);
}
